import { resolveCollectionSlug } from '@/domain/market/collection-slug.js';
import {
  ET_MONTH_INDEX,
  getEtDateParts,
  inferYearForMonthDay,
  resolveDailyUpdownWindowFromSettlementDate,
  buildDailyUpdownSlugFromStart,
  zonedEtToUtcSec,
} from '@/domain/crypto/crypto-updown-et-time.js';

/**
 * @typedef {{
 *   asset: string,
 *   interval: string,
 *   roundTs: number,
 *   seriesKey: string,
 *   feedGroupKey?: string,
 *   kind?: 'interval' | 'hourly' | 'daily',
 *   fullAsset?: string,
 *   endSec?: number,
 * }} MinuteUpdownSlug
 *
 * Interval 型（btc-updown-5m|{ts}）：slug 末尾 unix = 窗口结束（与 API endDate 一致）；
 * roundTs 一律为开盘 = end − interval；
 * hourly ET（bitcoin-up-or-down-august-12-2026-12pm-et）：roundTs = 命名小时开盘；
 * daily 型 roundTs 仍为 noon 开盘。
 */

/** `{asset}-updown-{5m|4h|24h|1d}-{ts}` */
const INTERVAL_UPDOWN_SLUG_RE = /^([a-z0-9]+)-updown-(\d+[mhd])-(\d+)$/i;

/** 自建日历日线：`{asset}-updown-daily-{endUnix}`（ET noon→noon，末尾 = 结束 noon） */
const SELF_BUILT_DAILY_UPDOWN_SLUG_RE = /^([a-z0-9]+)-updown-daily-(\d+)$/i;

/** `bitcoin-up-or-down-on-july-22-2026` */
const DAILY_UPDOWN_SLUG_RE =
  /^(bitcoin|ethereum|solana|xrp|dogecoin|bnb|hype)-up-or-down-on-([a-z]+)-(\d{1,2})(?:-(\d{4}))?$/i;

/** `bitcoin-up-or-down-august-12-2026-12pm-et`（PM 小时盘，命名小时 = 窗起点） */
const HOURLY_ET_UPDOWN_SLUG_RE =
  /^(bitcoin|ethereum|solana|xrp|dogecoin|bnb|hype)-up-or-down-([a-z]+)-(\d{1,2})-(\d{4})-(\d{1,2})(am|pm)-et$/i;

const FULL_ASSET_TO_SHORT = {
  bitcoin: 'btc',
  ethereum: 'eth',
  solana: 'sol',
  xrp: 'xrp',
  dogecoin: 'doge',
  bnb: 'bnb',
  hype: 'hype',
};

/** btcupdown-5m-123 → btc-updown-5m-123；btcupdown-4h-123 同理 */
export function normalizeMinuteUpdownSlugInput(slug) {
  let normalized = String(slug ?? '')
    .trim()
    .toLowerCase();
  if (!normalized) return normalized;
  normalized = normalized.replace(/^([a-z0-9]{2,12})updown-daily-(\d+)$/i, '$1-updown-daily-$2');
  normalized = normalized.replace(/^([a-z0-9]{2,12})updown-(\d+[mhd])-(\d+)$/i, '$1-updown-$2-$3');
  return normalized;
}

function toHour24(hour12, ampm) {
  const h = Number(hour12);
  const period = String(ampm ?? '')
    .trim()
    .toLowerCase();
  if (!Number.isFinite(h) || h < 1 || h > 12) return null;
  if (period === 'am') return h === 12 ? 0 : h;
  if (period === 'pm') return h === 12 ? 12 : h + 12;
  return null;
}

/**
 * 构建 interval 型 slug。roundTs = 开盘；slug 埋入结束时刻（开盘 + interval）。
 * 例：开盘 1785770400（5m）→ btc-updown-5m-1785770700
 */
export function buildMinuteUpdownSlug(asset, intervalLabel, roundTs) {
  const assetKey = String(asset ?? '')
    .trim()
    .toLowerCase();
  const interval = String(intervalLabel ?? '5m')
    .trim()
    .toLowerCase();
  const start = Number(roundTs);
  if (!assetKey || !interval || !Number.isFinite(start) || start <= 0) return '';
  if (interval === 'daily') {
    const parts = getEtDateParts(start);
    const startNoon = zonedEtToUtcSec(parts.year, parts.monthIndex, parts.day, 12, 0);
    if (startNoon !== start) return '';
    const next = new Date(Date.UTC(parts.year, parts.monthIndex, parts.day));
    next.setUTCDate(next.getUTCDate() + 1);
    const endTs = zonedEtToUtcSec(
      next.getUTCFullYear(),
      next.getUTCMonth(),
      next.getUTCDate(),
      12,
      0
    );
    if (!Number.isFinite(endTs) || endTs <= start) return '';
    return `${assetKey}-updown-daily-${endTs}`;
  }
  const intervalSec = resolveUpdownFeedIntervalSec(interval);
  const endTs = start + intervalSec;
  if (!Number.isFinite(endTs) || endTs <= start) return '';
  return `${assetKey}-updown-${interval}-${endTs}`;
}

function parseSelfBuiltDailyUpdownSlug(normalized) {
  const match = normalized.match(SELF_BUILT_DAILY_UPDOWN_SLUG_RE);
  if (!match) return null;
  const slugEndTs = Number(match[2]);
  if (!Number.isFinite(slugEndTs) || slugEndTs <= 0) return null;
  const parts = getEtDateParts(slugEndTs);
  if (parts.hour !== 12 || parts.minute !== 0) return null;
  const expectedEnd = zonedEtToUtcSec(parts.year, parts.monthIndex, parts.day, 12, 0);
  if (expectedEnd !== slugEndTs) return null;
  const window = resolveDailyUpdownWindowFromSettlementDate(
    parts.year,
    parts.monthIndex,
    parts.day
  );
  if (!window?.startSec || !window?.endSec) return null;
  return {
    asset: match[1],
    interval: 'daily',
    roundTs: window.startSec,
    endSec: window.endSec,
    seriesKey: `${match[1]}-updown-daily`,
    kind: 'interval',
  };
}

function parseIntervalUpdownSlug(normalized) {
  const selfBuiltDaily = parseSelfBuiltDailyUpdownSlug(normalized);
  if (selfBuiltDaily) return selfBuiltDaily;

  const match = normalized.match(INTERVAL_UPDOWN_SLUG_RE);
  if (!match) return null;

  const slugEndTs = Number(match[3]);
  if (!Number.isFinite(slugEndTs) || slugEndTs <= 0) return null;

  const interval = String(match[2]).toLowerCase();
  const intervalSec = resolveUpdownFeedIntervalSec(interval);
  const roundTs = slugEndTs - intervalSec;
  if (!Number.isFinite(roundTs) || roundTs <= 0) return null;

  return {
    asset: match[1],
    interval,
    roundTs,
    endSec: slugEndTs,
    seriesKey: `${match[1]}-updown-${interval}`,
    kind: 'interval',
  };
}

function parseHourlyEtUpdownSlug(normalized) {
  const match = normalized.match(HOURLY_ET_UPDOWN_SLUG_RE);
  if (!match) return null;

  const fullAsset = String(match[1]).toLowerCase();
  const monthKey = String(match[2]).toLowerCase();
  const monthIndex = ET_MONTH_INDEX[monthKey];
  const day = Number(match[3]);
  const year = Number(match[4]);
  const hour24 = toHour24(match[5], match[6]);
  if (monthIndex == null || !Number.isFinite(day) || !Number.isFinite(year) || hour24 == null) {
    return null;
  }

  const startSec = zonedEtToUtcSec(year, monthIndex, day, hour24, 0);
  if (!Number.isFinite(startSec) || startSec <= 0) return null;
  const endSec = startSec + 3600;
  const asset = FULL_ASSET_TO_SHORT[fullAsset] || fullAsset;
  return {
    asset,
    fullAsset,
    interval: '1h',
    roundTs: startSec,
    endSec,
    // Product identity (≠ self-built btc-updown-1h); feed stacking uses feedGroupKey.
    seriesKey: `${fullAsset}-up-or-down-1h`,
    feedGroupKey: `${asset}-updown-1h`,
    kind: 'hourly',
  };
}

function parseDailyUpdownSlug(normalized) {
  const match = normalized.match(DAILY_UPDOWN_SLUG_RE);
  if (!match) return null;

  const fullAsset = String(match[1]).toLowerCase();
  const monthKey = String(match[2]).toLowerCase();
  const monthIndex = ET_MONTH_INDEX[monthKey];
  const day = Number(match[3]);
  const year = match[4] ? Number(match[4]) : inferYearForMonthDay(monthIndex, day);
  if (monthIndex == null || !Number.isFinite(day) || !Number.isFinite(year)) return null;

  const window = resolveDailyUpdownWindowFromSettlementDate(year, monthIndex, day);
  if (!window) return null;

  const asset = FULL_ASSET_TO_SHORT[fullAsset] || fullAsset;
  return {
    asset,
    fullAsset,
    interval: '1d',
    roundTs: window.startSec,
    endSec: window.endSec,
    seriesKey: `${fullAsset}-up-or-down`,
    kind: 'daily',
  };
}

/**
 * 解析 updown slug：分钟/小时 interval、PM 小时 ET、或日盘 date 型。
 * @param {string} slug
 * @returns {MinuteUpdownSlug | null}
 */
export function parseMinuteUpdownSlug(slug) {
  const normalized = normalizeMinuteUpdownSlugInput(slug);
  if (!normalized) return null;
  return (
    parseIntervalUpdownSlug(normalized) ||
    parseHourlyEtUpdownSlug(normalized) ||
    parseDailyUpdownSlug(normalized)
  );
}

/** @deprecated 同 parseMinuteUpdownSlug；统一入口别名 */
export function parseCryptoUpdownSlug(slug) {
  return parseMinuteUpdownSlug(slug);
}

/** 分钟 updown 常见资产（相关市场按各 asset-updown-{interval} 查询） */
export const UPDOWN_MINUTE_ASSETS = ['btc', 'eth', 'sol', 'xrp', 'doge', 'bnb', 'hype'];

export function isUpdownMinuteAsset(asset) {
  const key = String(asset ?? '')
    .trim()
    .toLowerCase();
  return Boolean(key) && UPDOWN_MINUTE_ASSETS.includes(key);
}

/**
 * cryptoPageBySlug 系列前缀：
 * - interval：btc-updown-5m-1784724000 → btc-updown-5m（支持 5m/4h）
 * - daily：bitcoin-up-or-down-on-… → bitcoin-up-or-down
 * 非法值（如 "up or down-updown-5m"）返回空串。
 */
export function toUpdownSeriesCollSlug(slugOrSeriesKey) {
  const raw = String(slugOrSeriesKey ?? '')
    .trim()
    .toLowerCase();
  if (!raw) return '';

  const parsed = parseMinuteUpdownSlug(raw);
  if (parsed?.kind === 'daily' && parsed.seriesKey) return parsed.seriesKey;
  // 仅自建 interval 可进 cryptoPageBySlug 系列前缀；PM hourly / daily 不走 Updown 切盘 API
  if (parsed?.kind === 'interval' && parsed.seriesKey && isUpdownMinuteAsset(parsed.asset)) {
    return parsed.seriesKey;
  }

  if (/-up-or-down$/i.test(raw) && !/-up-or-down-on-/i.test(raw)) return raw;

  const stripped = raw.replace(/-(\d+)$/, '');
  const match = stripped.match(/^([a-z0-9]+)-updown-(daily|\d+[mhd])$/i);
  if (match && isUpdownMinuteAsset(match[1])) return `${match[1]}-updown-${match[2]}`;
  return '';
}

/**
 * 单档匹配用完整 slug（系列前缀 + 目标 roundTs / 日盘 date）。
 */
export function toUpdownRoundCollSlug(slugOrSeriesKey, roundTs) {
  const series = toUpdownSeriesCollSlug(slugOrSeriesKey);
  const ts = Number(roundTs);
  if (!series || !Number.isFinite(ts) || ts <= 0) return '';

  if (/-up-or-down$/i.test(series)) {
    return buildDailyUpdownSlugFromStart(series, ts);
  }

  const match = series.match(/^([a-z0-9]+)-updown-(daily|\d+[mhd])$/i);
  if (!match) return '';
  return buildMinuteUpdownSlug(match[1], match[2], ts);
}

export function isMinuteUpdownSlug(slug) {
  return parseMinuteUpdownSlug(slug) != null;
}

/** 4h / 日盘 / 分钟盘均可解析；导航进 Updown 切盘请用 isIntervalUpdownSlug */
export function isCryptoUpdownSlug(slug) {
  return parseMinuteUpdownSlug(slug) != null;
}

/**
 * 仅自建 interval Updown（含 ET 日历日线 daily）进 CryptoUpdownDetail。
 * PM 日盘 on-…、PM 小时 ET（…-12pm-et）走 MarketDetail。
 */
export function isIntervalUpdownSlug(slug) {
  const parsed = parseMinuteUpdownSlug(slug);
  if (!parsed) return false;
  if (parsed.kind === 'daily' || parsed.kind === 'hourly') return false;
  const interval = String(parsed.interval ?? '').toLowerCase();
  return interval === 'daily' || /^\d+[mhd]$/.test(interval);
}

export function isDailyUpdownSlug(slug) {
  return parseMinuteUpdownSlug(slug)?.kind === 'daily';
}

/** PM 小时 ET：bitcoin-up-or-down-august-12-2026-12pm-et */
export function isHourlyEtUpdownSlug(slug) {
  return parseMinuteUpdownSlug(slug)?.kind === 'hourly';
}

/** 仅 5m 分钟盘 */
export function isFiveMinuteUpdownSlug(slug) {
  return parseMinuteUpdownSlug(slug)?.interval === '5m';
}

/** 分钟盘 slug 来源：ticker 优先（展示时间 = ticker 末尾 unix 秒转本地时钟） */
export function resolveUpdownRoundSlugSource(row) {
  const candidates = [row?.ticker, row?.slug, row?.eventSlug, row?.gammaSlug];
  for (const raw of candidates) {
    const normalized = normalizeMinuteUpdownSlugInput(String(raw ?? '').trim());
    if (normalized && parseMinuteUpdownSlug(normalized)) return normalized;
  }
  return '';
}

/** 仅 BTC 分钟盘展示浮动 +$ 动效标签 */
export function isBtcMinuteUpdownSlug(slug) {
  const parsed = parseMinuteUpdownSlug(slug);
  return parsed?.asset === 'btc';
}

/** 从 updown 标题推断资产（btc/eth/...） */
export function inferAssetFromTitle(title) {
  const text = String(title ?? '').toLowerCase();
  if (/\bbitcoin\b|\bbtc\b/.test(text)) return 'btc';
  if (/\bethereum\b|\beth\b/.test(text)) return 'eth';
  if (/\bsolana\b|\bsol\b/.test(text)) return 'sol';
  if (/\bxrp\b|\bripple\b/.test(text)) return 'xrp';
  if (/\bdoge\b|\bdogecoin\b/.test(text)) return 'doge';
  if (/\bbnb\b|\bbinance\b/.test(text)) return 'bnb';
  if (/\bhype\b/.test(text)) return 'hype';
  return '';
}

const CHAINLINK_SYMBOLS = {
  btc: 'btc/usd',
  eth: 'eth/usd',
  sol: 'sol/usd',
  xrp: 'xrp/usd',
  doge: 'doge/usd',
  bnb: 'bnb/usd',
  hype: 'hype/usd',
};

export function assetToChainlinkSymbol(asset) {
  const key = String(asset ?? '')
    .trim()
    .toLowerCase();
  if (!key || key === 'crypto') return '';
  return CHAINLINK_SYMBOLS[key] || `${key}/usd`;
}

/** slug 或标题推断 Chainlink symbol（DeepSage 自建仓常无 polymarket slug） */
export function resolveUpdownChainlinkSymbol({ slug, title } = {}) {
  const parsed = parseMinuteUpdownSlug(slug);
  const asset = parsed?.asset || inferAssetFromTitle(title);
  return assetToChainlinkSymbol(asset);
}

/** Feed 卡片 slug：分钟盘优先 ticker 完整 slug（含 roundTs） */
export function resolveFeedCardCollectionSlug(row) {
  const updownSlug = resolveUpdownRoundSlugSource(row);
  if (updownSlug) return updownSlug;
  return resolveCollectionSlug(row);
}

/** @param {object} row */
function resolveMinuteUpdownSlugFromRow(row) {
  return resolveUpdownRoundSlugSource(row) || resolveCollectionSlug(row);
}

export function sortCryptoMinuteUpdownFeedRows(rows, { sortOrder = 'desc' } = {}) {
  if (!Array.isArray(rows) || rows.length <= 1) return rows ?? [];

  const factor = sortOrder === 'asc' ? 1 : -1;
  const indexed = rows.map((row, index) => {
    const parsed = parseMinuteUpdownSlug(resolveMinuteUpdownSlugFromRow(row));
    return { row, index, parsed };
  });

  indexed.sort((a, b) => {
    const aTs = a.parsed?.roundTs;
    const bTs = b.parsed?.roundTs;
    if (aTs == null && bTs == null) return a.index - b.index;
    if (aTs == null) return 1;
    if (bTs == null) return -1;
    const tsDiff = (aTs - bTs) * factor;
    if (tsDiff !== 0) return tsDiff;
    const seriesDiff = String(a.parsed.seriesKey).localeCompare(String(b.parsed.seriesKey));
    if (seriesDiff !== 0) return seriesDiff;
    return a.index - b.index;
  });

  return indexed.map((entry) => entry.row);
}

/**
 * Feed 去重距离：live=0 优先；未来=距开盘秒数；已结束大幅惩罚（避免后半窗误选下一档）。
 */
export function resolveUpdownFeedIntervalSec(seriesKeyOrInterval) {
  const raw = String(seriesKeyOrInterval ?? '')
    .trim()
    .toLowerCase();
  if (!raw) return 300;
  if (
    raw === 'daily' ||
    raw.endsWith('-updown-daily') ||
    raw === '1d' ||
    raw.endsWith('-up-or-down') ||
    /(?:^|-)1d$/.test(raw)
  ) {
    return 86400;
  }
  const dayMatch = raw.match(/(\d+)d$/);
  if (dayMatch) {
    const days = Number(dayMatch[1]);
    if (Number.isFinite(days) && days > 0) return days * 86400;
  }
  const hourMatch = raw.match(/(\d+)h$/);
  if (hourMatch) {
    const hours = Number(hourMatch[1]);
    if (Number.isFinite(hours) && hours > 0) return hours * 3600;
  }
  const minMatch = raw.match(/(\d+)m$/);
  if (minMatch) {
    const mins = Number(minMatch[1]);
    if (Number.isFinite(mins) && mins > 0) return mins * 60;
  }
  return 300;
}

export function updownRoundFeedDistance(roundTs, nowSec, intervalSec = 300) {
  const start = Number(roundTs);
  const now = Number(nowSec);
  const interval = Math.max(Number(intervalSec) || 300, 60);
  if (!Number.isFinite(start) || start <= 0 || !Number.isFinite(now) || now <= 0) {
    return Number.POSITIVE_INFINITY;
  }
  if (start <= now && now < start + interval) return 0;
  if (now < start) return start - now;
  return now - (start + interval) + 1_000_000_000;
}

function parseFeedRowIsoSec(...candidates) {
  for (const raw of candidates) {
    const ms = Date.parse(String(raw ?? '').trim());
    if (Number.isFinite(ms) && ms > 0) return Math.floor(ms / 1000);
  }
  return null;
}

/** Feed 行开盘秒：日盘以结算日 slug 为准；interval 优先 API 窗口。 */
function resolveUpdownStartSecFromRow(row, parsed) {
  // 日盘 startDate 可能是平台创建时间，并非 noon-to-noon 盘口开盘。
  if (parsed?.kind === 'daily' && Number(parsed.roundTs) > 0) {
    return Number(parsed.roundTs);
  }

  const market = Array.isArray(row?.markets) ? row.markets[0] : null;
  const apiStart = parseFeedRowIsoSec(
    row?.eventStartTime,
    row?.startDate,
    market?.eventStartTime,
    market?.startDate
  );
  const apiEnd = parseFeedRowIsoSec(row?.endDate, market?.endDate);
  if (apiStart != null && apiEnd != null && apiEnd > apiStart) return apiStart;
  if (apiStart != null) return apiStart;
  if (parsed?.roundTs) return Number(parsed.roundTs);
  if (apiEnd != null) {
    const intervalSec = resolveUpdownFeedIntervalSec(parsed?.interval || parsed?.seriesKey);
    return apiEnd - intervalSec;
  }
  return 0;
}

/**
 * Feed 堆叠键：币种×周期。PM 小时与自建 1h 同桶；SeriesKey 仍按产品线区分。
 * @param {MinuteUpdownSlug | null | undefined} parsed
 */
export function resolveUpdownFeedGroupKey(parsed) {
  if (!parsed) return '';
  const explicit = String(parsed.feedGroupKey ?? '').trim();
  if (explicit) return explicit;
  if (parsed.kind === 'hourly' && parsed.asset) return `${parsed.asset}-updown-1h`;
  return String(parsed.seriesKey ?? '').trim();
}

/**
 * Crypto Updown Feed：按 seriesKey 去重；同系列优先 live（距离 0），
 * 其次最近未来，已结束垫底。
 * @template T
 * @param {T[]} rows
 * @param {{ sortOrder?: 'asc' | 'desc', nowSec?: number }} [options]
 * @returns {T[]}
 */
export function normalizeCryptoMinuteUpdownFeedRows(rows, options = {}) {
  return dedupeCryptoMinuteUpdownRows(rows, options);
}

/**
 * 同一币种同一周期（feedGroupKey / seriesKey）
 * 在首页/探索/加密 Feed 只留当前档；无法解析的普通市场原样保留。
 * @template T
 * @param {T[]} rows
 * @param {{ nowSec?: number }} [options]
 * @returns {T[]}
 */
export function dedupeCryptoMinuteUpdownRows(rows, { nowSec } = {}) {
  if (!Array.isArray(rows) || rows.length <= 1) return rows ?? [];

  const now =
    Number.isFinite(Number(nowSec)) && Number(nowSec) > 0
      ? Math.floor(Number(nowSec))
      : Math.floor(Date.now() / 1000);

  /** @type {Map<string, { row: T, index: number, dist: number, intervalSec: number }>} */
  const bestBySeries = new Map();
  /** @type {{ row: T, index: number }[]} */
  const passthrough = [];

  rows.forEach((row, index) => {
    const parsed = parseMinuteUpdownSlug(resolveMinuteUpdownSlugFromRow(row));
    if (!parsed) {
      passthrough.push({ row, index });
      return;
    }

    const seriesGroupKey = resolveUpdownFeedGroupKey(parsed);
    if (!seriesGroupKey) {
      passthrough.push({ row, index });
      return;
    }
    const intervalSec = resolveUpdownFeedIntervalSec(parsed.interval || parsed.seriesKey);
    const startSec = resolveUpdownStartSecFromRow(row, parsed);
    const dist = updownRoundFeedDistance(startSec, now, intervalSec);
    const prev = bestBySeries.get(seriesGroupKey);
    const isBetter =
      !prev ||
      dist < prev.dist ||
      (dist === prev.dist && intervalSec < prev.intervalSec) ||
      (dist === prev.dist && intervalSec === prev.intervalSec && index < prev.index);

    if (isBetter) {
      bestBySeries.set(seriesGroupKey, { row, index, dist, intervalSec });
    }
  });

  return [...passthrough, ...bestBySeries.values()]
    .sort((a, b) => a.index - b.index)
    .map((entry) => entry.row);
}

export { buildDailyUpdownSlugFromStart };
